import { Alert } from 'antd';
import { useIsMutating } from '@tanstack/react-query';
import { useIsOnline } from '../../lib/online-status';

/**
 * "The office's internet is not the developer's"
 * (docs/07-implementation-roadmap.md F7.5).
 *
 * One banner in the shell rather than an offline state on every screen, because
 * the condition is global and its consequences are uniform: with
 * `networkMode: 'online'` (the default this app keeps), TanStack Query does not
 * start a request while the browser reports no connection. Queries **pause**
 * instead of failing, and mutations **queue** and run when the connection comes
 * back.
 *
 * That behaviour is good and it is invisible, which is the problem this banner
 * solves. Without it:
 *
 * - a list sits on "Memuat…" with no request in flight, indistinguishable from
 *   a slow one;
 * - a dialog's "Simpan" spins with no error and no result, and the staff member
 *   clicks it again, or closes the dialog and loses the work — while the
 *   mutation is in fact parked and will complete by itself;
 * - nothing anywhere says the word "koneksi", so the office reports a broken
 *   application.
 *
 * So the banner says what is happening *and* what will happen, and it says the
 * second part more loudly when there is a write waiting, because that is the
 * moment somebody is deciding whether to click again.
 *
 * `useIsMutating` counts paused mutations too — a paused mutation is still
 * pending, which is exactly the condition worth naming here.
 */
export function OfflineBanner() {
  const online = useIsOnline();
  const pendingWrites = useIsMutating();

  if (online) return null;

  return (
    <Alert
      type="warning"
      showIcon
      banner
      title="Tidak ada koneksi internet"
      description={
        pendingWrites > 0
          ? 'Perubahan yang Anda simpan sedang menunggu dan akan terkirim sendiri begitu koneksi kembali. Jangan tutup halaman ini, dan tidak perlu menyimpan ulang.'
          : 'Halaman ini menampilkan data terakhir yang sempat dimuat. Pemuatan dan penyimpanan akan berjalan sendiri begitu koneksi kembali.'
      }
    />
  );
}
